import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { journeys as journeysApi, attributes as attributesApi, labels as labelsApi } from '../../../apiService/blustreamApi';
import { fetchAll } from './useJourneyData';
import { queryKeys } from './queries';

// Create (POST) or edit (PATCH) a touchpoint, then refresh the timeline's data.
export function useTouchpointMutation(journeyId) {
  const qc = useQueryClient();
  return useMutation(
    ({ mode, touchpointId, body }) =>
      mode === 'create'
        ? journeysApi.createTouchpoint(journeyId, body)
        : journeysApi.updateTouchpoint(journeyId, touchpointId, body),
    {
      onSuccess: async () => {
        // Remove (not just invalidate) the journey's touchpoints: the detail query reads
        // them via ensureQueryData, which serves stale-but-cached data and never refetches
        // a query with no observer. Removing forces a fresh fetch on the detail rebuild, so
        // a saved ordinal/label change shows immediately instead of only after a refresh.
        qc.removeQueries(queryKeys.touchpoints(Number(journeyId)));
        // AWAIT the detail refetch: the mutation only reaches 'success' (which fires the
        // caller's onSuccess → closes the drawer / drops the live preview) after this
        // resolves. That keeps the preview showing the new position until fresh data lands,
        // instead of briefly falling back to the stale (old-position) cache — no flicker.
        await qc.invalidateQueries(['journeyDetail']);
      },
    },
  );
}

// The beta API serializes AttributeType via @JsonValue as "Boolean"/"Text"/"Number"/
// "DateTime"; normalize to the canonical keys the segment builder's operatorsForType
// map uses (BOOLEAN/TEXT/NUMBER/DATE_TIME). Unknown values pass through unchanged.
const ATTRIBUTE_TYPE = { Boolean: 'BOOLEAN', Text: 'TEXT', Number: 'NUMBER', DateTime: 'DATE_TIME' };

// Account attribute library → select options. Cached; shared across drawers.
export function useAttributeOptions() {
  return useQuery(['attributeOptions'], async ({ signal }) => {
    const all = await fetchAll((page) => attributesApi.list({ page, size: 200, signal }));
    return all.map((a) => ({ value: String(a.attribute_id), label: a.name, type: ATTRIBUTE_TYPE[a.type] || a.type }));
  });
}

// Account labels → select options (labels.list may return an array or a paged body).
export function useLabelOptions() {
  return useQuery(['labelOptions'], async ({ signal }) => {
    const res = await labelsApi.list({ signal });
    const items = Array.isArray(res) ? res : (res && res.content) || [];
    return items.map((l) => ({ value: l.uuid, label: l.name, type: l.type }));
  });
}

// Hard-delete a touchpoint (DELETE /journeys/:jid/touchpoints/:tid), then remove the
// journey's touchpoints cache so the detail query refetches fresh (it reads via
// ensureQueryData, which won't refetch a merely-invalidated observer-less query).
export function useDeleteTouchpoint() {
  const qc = useQueryClient();
  return useMutation(
    ({ journeyId, touchpointId }) => journeysApi.removeTouchpoint(journeyId, touchpointId),
    {
      onSuccess: async (_data, vars) => {
        qc.removeQueries(queryKeys.touchpoints(Number(vars.journeyId)));
        await qc.invalidateQueries(['journeyDetail']);
      },
    },
  );
}
